import { Heading } from '@chakra-ui/react';
import {
  QuoteShareAccessRequest,
  RecordShareEngagement,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useFlags } from '../../../services/launchDarkly';
import { logger } from '../../../services/logger';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MInput,
  MText,
} from '../../Monetize';
import { CopyToClipboardButton } from '../../Monetize/CopyToClipboardButton';
import { DatePicker } from '../../Monetize/DatePicker/DatePicker';
import { QuoteCollaborationRevokeLink } from './QuoteCollaborationRevokeLink';
import { QuoteShareActivity } from './QuoteShareActivity';
import { useQuoteShare } from './useQuoteShare';

interface QuoteShareFormAndActivityProps {
  quoteId: string;
  quoteShareData: ReturnType<typeof useQuoteShare>;
  engagements?: RecordShareEngagement[];
  isDisabled?: boolean;
}

/**
 * Allow users to create a Quote Share, view/copy an existing share link,
 * and view external activity.
 */
export const QuoteShareFormAndActivity = ({
  quoteId,
  quoteShareData,
  engagements,
  isDisabled,
}: QuoteShareFormAndActivityProps) => {
  const [isShareCreated, setIsShareCreated] = useState(false);
  const { enableQuoteShareRedirect } = useFlags();
  const {
    dataQuery: { addShareQuery, getQuoteShareQuery, revokeShareQuery },
    expires,
    externalShareState,
    formData,
    handleExternalShare,
  } = quoteShareData;

  const handleCreateShare = async (shareData: QuoteShareAccessRequest) => {
    await handleExternalShare(shareData);
    setIsShareCreated(true);
  };

  const handleRevokeShare = (sharedId: string) => {
    revokeShareQuery.mutate({
      recordShareId: sharedId,
      quoteId,
    });
    setIsShareCreated(false);
  };

  return (
    <>
      {externalShareState.canCreateShareLink && (
        <form
          onSubmit={formData.handleSubmit(handleCreateShare, (err) =>
            logger.warn(err),
          )}
        >
          {enableQuoteShareRedirect && (
            <MBox mt={2}>
              <MFormField
                label="Redirect URL"
                error={formData.formState.errors?.redirectUrl}
                tooltip="An optional URL where the user will be redirected after accepting the quote. If not provided, the user will remain on the current page."
              >
                <Controller
                  name="redirectUrl"
                  control={formData.control}
                  render={({ field }) => (
                    <MInput
                      placeholder="https://"
                      isDisabled={addShareQuery.isPending || isDisabled}
                      {...field}
                      value={field.value ?? ''}
                    />
                  )}
                />
              </MFormField>
            </MBox>
          )}
          <MFlex alignItems="end" justifyContent="space-between" mt={2}>
            <MBox>
              <MFormField
                label="Expiration Date"
                error={formData.formState.errors?.expiresAt}
              >
                <Controller
                  name="expiresAt"
                  control={formData.control}
                  render={({ field }) => (
                    <DatePicker
                      minDate={expires.minExpiresAt}
                      maxDate={expires.maxExpiresAt}
                      isDisabled={addShareQuery.isPending || isDisabled}
                      {...field}
                    />
                  )}
                />
              </MFormField>
            </MBox>
            <MBox>
              <MButton
                variant="primary"
                isLoading={addShareQuery.isPending}
                isDisabled={
                  addShareQuery.isPending || isShareCreated || isDisabled
                }
                type="submit"
              >
                Create Link
              </MButton>
            </MBox>
          </MFlex>
        </form>
      )}

      {externalShareState.hasRecordShareRecords && (
        <>
          <Heading as="h4" fontWeight={500} fontSize="sm" mt={4} mb={2}>
            Access
          </Heading>
          <MBox maxH="25vh" overflowY="auto">
            {getQuoteShareQuery.data?.map(({ id, expiresAt, url }) => (
              <MBox key={id} mb={1} py={1} pr={1}>
                <MFlex alignItems="center" justifyContent="space-between">
                  <MFlex flexDir="column">
                    <MText>Expires: {toDateShort(expiresAt)}</MText>
                  </MFlex>
                  <MFlex>
                    <QuoteCollaborationRevokeLink
                      isLoading={revokeShareQuery.isPending}
                      onRevoke={() => handleRevokeShare(id)}
                      mr={2}
                    />
                    <CopyToClipboardButton value={url} />
                  </MFlex>
                </MFlex>
              </MBox>
            ))}
          </MBox>
        </>
      )}

      {externalShareState.showExternalActivity && (
        <QuoteShareActivity engagements={engagements} />
      )}
    </>
  );
};
